import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import PropTypes from 'prop-types';

// Components
import HeaderChart, { VIEW_MODE } from '@/components/composite/Charts/components/HeaderChart';
import LegendCustom from '@/components/composite/Charts/components/LegendCustom';
import PostingHabitsLegend from '@/components/composite/Charts/components/PostingHabitsLegend';
import NoDataChart from '@/components/composite/Charts/components/NoDataChart';
import AggregatedNetworksList from '@/components/composite/Analyze/AggregatedNetworksList';

// Lib
import useScreenshot from '@/hooks/utils/useScreenshot';
import { ignoredPattern } from '@/lib/analytics/charts/legendIgnoredPattern';

// Hooks
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';

export const ChartWrapperContent = ({
    children,
    screenshotRef,
    className,
    isLoading,
    legendType = 'line',
    setRequestParams,
    activeFilter,
    setActiveFilter,
    setState,
    data,
    onDelete = () => {
        /** */
    },
    isDashboard = false,
    isExport = false,
    exportData = {},
    competitor,
    isComparativeViewActive,
    toggleComparativeView,
    ...restProps
}) => {
    const { generatePng } = useScreenshot();
    const { t } = useTranslation('analytics');
    const [activeTable, setActiveTable] = useState(
        data?.noChartView ? true : data?.data?.viewMode === VIEW_MODE.TABLE
    );
    const { spGroups } = useSPGroups({
        revalidateIfStale: false
    });
    const isNumberLegend = data.id === 'posts_distribution';
    const group = exportData?.group ?? spGroups?.find((group) => group.id === data?.groups?.[0]);

    const finalData = useMemo(() => {
        if (data) {
            let result = data;

            // some graphData should not be displayed => disableGraphData in graph config
            let graphData = [];
            if (data?.disableGraphData?.length > 0) {
                result?.graphData?.map((item) => {
                    const obj = { ...item };
                    for (const dtd of data?.disableGraphData ?? []) {
                        delete obj[dtd];
                    }
                    graphData?.push(obj);
                });
                result.graphData = graphData;
            }

            return result;
        }
        return {};
    }, [data]);

    const columnNames = useMemo(() => {
        let columnNames = {};

        if (finalData.graphData?.length && !finalData?.noTableView) {
            Object.keys(finalData.graphData[0])?.map((item) => {
                if (item !== 'avg' && item !== 'no_data') {
                    let nameLegend = finalData.name[item];
                    if (typeof nameLegend === 'object' && activeFilter) {
                        nameLegend = nameLegend[activeFilter];
                    }
                    columnNames[item] = isNumberLegend
                        ? nameLegend
                        : nameLegend?.raw || t(nameLegend);
                }
            });
        }

        return columnNames;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finalData, activeFilter, t]);

    const hasData = useMemo(() => {
        if (finalData.dataValuesProperties) {
            let existingDataValuesProperties = [];
            data.dataValuesProperties.forEach((dataValueProperty) => {
                if (
                    finalData.graphData[0] &&
                    Object.keys(finalData.graphData[0]).includes(dataValueProperty)
                ) {
                    existingDataValuesProperties.push(dataValueProperty);
                }
            });
            return {
                hasData: finalData.graphData?.length > 0 && finalData.max !== 0,
                legend: existingDataValuesProperties.length > 1 ? LegendCustom : null
            };
        } else {
            switch (data.id) {
                case 'posting_habits':
                    return {
                        hasData: finalData.graphData?.length > 0,
                        legend: PostingHabitsLegend
                    };

                case 'media_life_span':
                    return {
                        hasData:
                            finalData.graphData?.length > 0 &&
                            (finalData.media || finalData.previous_media),
                        legend: LegendCustom
                    };

                default:
                    return {
                        hasData: finalData.graphData?.length > 0 && finalData.max !== 0,
                        legend: LegendCustom
                    };
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.id, finalData]);

    const [hiddenKeys, setHiddenKeys] = useState({});

    useEffect(() => {
        let hiddenKeysTmp = {};

        if (finalData?.graphData?.length > 0) {
            if (data.id === 'reactions_distribution') {
                Object.values(finalData?.graphData).forEach((value) => {
                    hiddenKeysTmp[value.reaction] = false;
                });
            } else {
                Object.keys(finalData?.graphData?.[0]).forEach((key) => {
                    if (
                        finalData.dataValuesProperties
                            ? finalData.dataValuesProperties.includes(key)
                            : !ignoredPattern.includes(key)
                    ) {
                        hiddenKeysTmp[key] = false;
                    }
                });
            }
        }
        setHiddenKeys(hiddenKeysTmp);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.id, finalData?.graphData]);

    const handleToggleKeysLegend = (lineId) => {
        setHiddenKeys((prevState) => ({
            ...prevState,
            [lineId]: !prevState[lineId]
        }));
    };

    return (
        <>
            {!isLoading && (
                <HeaderChart
                    handleSwitchTable={() => setActiveTable(!activeTable)}
                    generatePng={() =>
                        generatePng(screenshotRef.current, `chart_${finalData.id}`, {
                            filter: (node) =>
                                node.id !== `chart_${finalData.id}` &&
                                node.id !== `help_${finalData.id}`
                        })
                    }
                    data={finalData}
                    onDelete={onDelete}
                    activeTable={activeTable}
                    columnNames={columnNames}
                    setRequestParams={setRequestParams}
                    setState={setState}
                    activeFilter={activeFilter}
                    setActiveFilter={setActiveFilter}
                    isDashboard={isDashboard}
                    isExport={isExport}
                    exportData={exportData}
                    isComparativeViewActive={isComparativeViewActive}
                    toggleComparativeView={toggleComparativeView}>
                    {finalData.title
                        ? t(finalData.title)
                        : activeFilter
                          ? t(finalData?.filters[activeFilter]?.title)
                          : ''}
                </HeaderChart>
            )}

            {hasData.hasData ? (
                <>
                    {children ? (
                        <div
                            className={clsx(
                                'flex w-full flex-col space-y-8 md:flex-row',
                                className,
                                { 'text-icoBlack': isExport }
                            )}>
                            {React.Children.map(children, (child) => {
                                const newChild = React.cloneElement(child, {
                                    data: finalData,
                                    height: finalData.height,
                                    columnNames,
                                    activeTable,
                                    activeFilter,
                                    isDashboard,
                                    isExport,
                                    hiddenKeys,
                                    ...restProps
                                });
                                return newChild;
                            })}
                        </div>
                    ) : null}

                    {/* Using a custom Legend component because there is an issue with overlapping elements, ResponsiveContainer and Legends from recharts */}
                    {hasData.legend && !finalData.noLegend && !activeTable ? (
                        <hasData.legend
                            isExport={isExport}
                            data={finalData}
                            activeFilter={activeFilter}
                            type={legendType}
                            competitor={competitor}
                            hiddenKeys={hiddenKeys}
                            handleToggleKeysLegend={handleToggleKeysLegend}
                            className={clsx('bg-icoGray-300', {
                                'dark:bg-icoDarkMode-deepBlack': !isExport,
                                'text-icoBlack': isExport
                            })}
                        />
                    ) : null}
                    {group ? (
                        <AggregatedNetworksList
                            group={group}
                            aggregatedNetworkTypes={data.aggregatedNetworks}
                            className="mt-3 flex flex-row items-center justify-end space-x-2"
                        />
                    ) : null}
                </>
            ) : (
                <NoDataChart data={data} isExport={isExport} exportData={exportData} />
            )}
        </>
    );
};

ChartWrapperContent.propTypes = {
    children: PropTypes.node.isRequired,
    screenshotRef: PropTypes.object,
    className: PropTypes.string,
    isLoading: PropTypes.bool,
    legendType: PropTypes.string,
    setRequestParams: PropTypes.func.isRequired,
    activeFilter: PropTypes.string,
    setActiveFilter: PropTypes.func.isRequired,
    setState: PropTypes.func,
    data: PropTypes.object.isRequired,
    onDelete: PropTypes.func,
    isDashboard: PropTypes.bool,
    isExport: PropTypes.bool,
    exportData: PropTypes.object,
    competitor: PropTypes.object,
    isComparativeViewActive: PropTypes.bool,
    toggleComparativeView: PropTypes.func
};
